import type { TabControlConfig } from '@/components/TabControl';
import { Modal } from '@/components/utils/Modal';
import { dashboardService } from '@/services/dashboardService';
import type { LayerMicroSP } from '@/types/dashboard';
import React, { useCallback, useEffect, useState } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';

import './ImportFromJira.css';

interface Layer {
  LayerId: number;
  Name: string;
  [key: string]: any;
}

interface Dashboard {
  DashboardId: number;
  [key: string]: any;
}

interface JiraCard {
  summary: string | null;
  issueType: string | null;
  status: string | null;
  assignee: string | null;
  estimated: number | null;
  remaining: number | null;
  startDate: Date | null;
  dueDate: Date | null;
  sp: number | null;
  description: string | null;
}

interface ExcelRow {
  Summary?: string;
  'Issue Type'?: string;
  Status?: string;
  Assignee?: string;
  Estimated?: string | number;
  Remaining?: string | number;
  'Start Date'?: string;
  'Due Date'?: string;
  SP?: string | number;
  Description?: string;
}

interface ImportPayload {
  listJiraCards: JiraCard[];
  layerIds: string;
  mainLayerId: number;
  projectId: number;
  dashboardId: number;
}

interface ImportFromJiraProps {
  open: boolean;
  closeModal: (open: boolean) => void;
  config: TabControlConfig;
  selectedDashboard: Dashboard | null;
  ifjMoveToModal: (dashboardId: number, dashboard: Dashboard) => void | undefined;
}

const buildSelectStyles = (isDark: boolean) => ({
  control: (base: any) => ({
    ...base,
    minHeight: 30,
    fontSize: 11,
    borderColor: isDark ? '#4a4c4c' : '#ccc',
    backgroundColor: isDark ? '#3a3c3c' : base.backgroundColor,
    boxShadow: 'none',
    '&:hover': { borderColor: isDark ? '#6e6860' : '#aaa' },
  }),
  menu: (base: any) => ({
    ...base,
    marginTop: 0,
    backgroundColor: isDark ? '#333535' : base.backgroundColor,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 12,
    padding: '5px 12px',
    backgroundColor:
      state.isSelected || state.isFocused ? '#62accb' : isDark ? '#333535' : undefined,
    color: state.isSelected || state.isFocused ? '#fff' : isDark ? '#a9a093' : '#333',
    cursor: 'pointer',
    '&:active': { backgroundColor: '#508ca6' },
  }),
  singleValue: (base: any) => ({ ...base, color: isDark ? '#a9a093' : base.color }),
  input: (base: any) => ({ ...base, color: isDark ? '#a9a093' : base.color }),
  placeholder: (base: any) => ({ ...base, color: isDark ? '#6e6860' : base.color }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: isDark ? '#3a3c3c' : '#fff',
    borderColor: isDark ? '#4a4c4c' : '#ccc',
    borderWidth: '1px',
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    fontSize: '10px',
    color: isDark ? '#a9a093' : base.color,
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    fontSize: '4px',
    color: isDark ? '#6e6860' : '#ccc',
    ':hover': { backgroundColor: isDark ? '#3a3c3c' : '#fff' },
  }),
  dropdownIndicator: (base: any) => ({ ...base, color: isDark ? '#6e6860' : base.color }),
  clearIndicator: (base: any) => ({ ...base, color: isDark ? '#6e6860' : base.color }),
  valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
});

export const ImportFromJira = ({
  open,
  closeModal,
  config,
  selectedDashboard,
  ifjMoveToModal,
}: ImportFromJiraProps) =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildSelectStyles(isDark);

  // State management
  const [projectLayers, setProjectLayers] = useState<LayerMicroSP[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<LayerMicroSP[]>([]);
  const [mainSelectedLayer, setMainSelectedLayer] = useState<LayerMicroSP | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  useEffect(() =>
  {
    const loadProjectLayers = async () =>
    {
      try
      {
        if (!open) return;
        const data = await dashboardService.GetLayersByProjectId(String(config.projectId));
        setProjectLayers(data as any);
      }
      catch (error)
      {
        console.error('Error loading project layers:', error);
      }
    };

    loadProjectLayers();
  }, [open]);

  // Handle modal close
  const handleCloseModal = () =>
  {
    closeModal(false);
    setProjectLayers([]);
    setSelectedLayers([]);
    setMainSelectedLayer(null);
    setSelectedFile(null);
    setResponseMessage('');
    setIsLoading(false);
    setErrorMessages([]);
  };

  const handleSelectLayerChange = useCallback((itemsSelected: Layer[]) =>
  {
    setSelectedLayers(itemsSelected as any);
    setMainSelectedLayer((itemsSelected.length === 1 ? itemsSelected[0] : null) as any);
  }, []);

  // Handle main layer selection change
  const handleSelectMainLayerChange = useCallback((itemSelected: Layer) =>
  {
    setMainSelectedLayer(itemSelected as any);
  }, []);

  // Handle file input change
  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = event.target.files?.[0];
    setSelectedFile(file || null);
  }, []);

  // Handle selector opened (remove is-open class)
  const handleSelectorOpened = useCallback((elementId: string) =>
  {
    const element = document.getElementById(elementId)?.querySelector('div.is-open');
    if (element)
    {
      element.classList.remove('is-open');
    }
  }, []);
  void handleSelectorOpened;

  // Download template file
  const getTemplateFile = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
  {
    e.preventDefault();
    const link = document.createElement('a');
    link.href = '/files/JiraTemplate.xlsx';
    link.download = 'JiraTemplate.xlsx';
    link.click();
  }, []);

  // Import cards from Jira
  const importCardsFromJira = useCallback(
    async (payload: ImportPayload) =>
    {
      setIsLoading(true);

      try
      {
        const data = (await dashboardService.ImportCardsFromJira(payload)) as any;

        if (data !== '' && data[0] && typeof data[0] === 'string')
        {
          const response = data[0] as string;

          // Check if there are error messages
          if (response.includes(';'))
          {
            const cardsCreated = response.split(';');
            const errorMessage = (cardsCreated[1] as string)
              .split('.')
              .filter((error: string) => error !== '');

            setResponseMessage(cardsCreated[0] as string);
            setErrorMessages(errorMessage);
          }
          else
          {
            setResponseMessage(response);
          }
        }

        ifjMoveToModal(selectedDashboard!.DashboardId, selectedDashboard!);
      }
      catch (error: any)
      {
        console.error('Error importing cards:', error);
      }
      finally
      {
        setIsLoading(false);
      }
    },
    [selectedDashboard, ifjMoveToModal],
  );

  const parseDataFromFile = //useCallback(
    () =>
    {
      if (!selectedFile || !mainSelectedLayer) return;

      const reader = new FileReader();

      reader.onload = e =>
      {
        const data = e.target?.result;

        try
        {
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0]!;
          const excelData = XLSX.utils.sheet_to_json<ExcelRow>(workbook.Sheets[sheetName]!);

          if (excelData.length === 0)
          {
            throw new Error('No valid data found in the file.');
          }

          // Transform data as required
          const transformedData: JiraCard[] = excelData.map(row => ({
            summary: row.Summary || null,
            issueType: row['Issue Type'] || null,
            status: row.Status || null,
            assignee: row.Assignee || null,
            estimated: row.Estimated ? parseFloat(String(row.Estimated)) : null,
            remaining: row.Remaining ? parseFloat(String(row.Remaining)) : null,
            startDate: row['Start Date']
              ? new Date(row['Start Date'].split('/').reverse().join('-'))
              : null,
            dueDate: row['Due Date']
              ? new Date(row['Due Date'].split('/').reverse().join('-'))
              : null,
            sp: row.SP ? parseInt(String(row.SP)) : null,
            description: row.Description ? row.Description.substring(0, 2500) : null,
          }));

          // Prepare the payload for API
          const payload: ImportPayload = {
            listJiraCards: transformedData,
            layerIds: selectedLayers.map(layer => layer.LayerId).join(','),
            mainLayerId: mainSelectedLayer.LayerId,
            projectId: config.projectId ?? 0,
            dashboardId: selectedDashboard?.DashboardId ?? 0,
          };

          // Call the API
          importCardsFromJira(payload);
        }
        catch (error)
        {
          console.error('Error parsing file:', error);
        }
      };

      reader.onerror = error =>
      {
        console.error('Error reading file:', error);
      };

      reader.readAsBinaryString(selectedFile);
    }; //, [selectedFile, selectedLayers, mainSelectedLayer, config.projectId, selectedDashboard.DashboardId, importCardsFromJira]);

  // Determine if import button should be disabled
  const isImportDisabled = selectedFile === null || mainSelectedLayer === null || isLoading;

  return (
    <Modal
      open={open}
      onClose={() => handleCloseModal()}
      title={`Import from Jira`}
      preventCloseClickOut
    >
      <div className="modal-dialog ifj-modal-dialog max-w-[368px]">
        <div className="modal-content">
          {responseMessage === '' && (
            <div>
              {/* Step 1: File Picker */}
              <div className="ifj-modal-body-divider">
                <div className="corgee-tool-module-instructions">
                  <span>Step 1:</span> Choose a file to import (Excel File).
                </div>

                <input
                  className="text-sm"
                  type="file"
                  id="inputFileId"
                  name="file"
                  accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleFileChange}
                />
              </div>

              {/* Step 2: Layer Selection */}
              <div className="ifj-modal-body-divider">
                <div className="corgee-tool-module-instructions">
                  <span>Step 2:</span> Select layers where this information will be imported.
                </div>

                {/* Project Layer(s) */}
                <div className="ifj-modal-input-container">
                  <label className="stime-label pull-left flex-1 dark:text-[#a9a093]">
                    Layer(s):
                  </label>
                  <div
                    id="select-render-ifj-Layers"
                    className="corgee_com_custom-fields-combobox-multiselect _DEL_comboBoxRef _DEL_multiselect-hidden-control-container input-group flex-[3]"
                  >
                    <Select
                      {...({} as any)}
                      options={projectLayers}
                      onChange={handleSelectLayerChange as any}
                      value={selectedLayers}
                      isMulti
                      onBlur={() => {}}
                      searchable={true}
                      openOnFocus={true}
                      autosize={false}
                      closeOnSelect={false}
                      getOptionLabel={(option: LayerMicroSP) => option.Name}
                      getOptionValue={(option: LayerMicroSP) => String(option.LayerId)}
                      styles={selectStyles}
                    />
                  </div>
                </div>
              </div>

              {/* Main Layer Selection (if multiple layers selected) */}
              {selectedLayers.length > 1 && (
                <div className="ifj-modal-body-divider ifj-modal-input-container ">
                  <label className="stime-label pull-left flex-1">Main Layer:</label>
                  <div
                    id="select-render-ifj-Main-Layer"
                    className="corgee_com_custom-fields-combobox-singleselect _DEL_comboBoxRef input-group flex-[3]"
                  >
                    <Select
                      {...({} as any)}
                      autoFocus={false}
                      options={selectedLayers}
                      onChange={handleSelectMainLayerChange as any}
                      value={mainSelectedLayer}
                      multi={false}
                      onBlur={() => {}}
                      searchable={true}
                      openOnFocus={true}
                      autosize={false}
                      closeOnSelect={true}
                      getOptionLabel={(option: LayerMicroSP) => option.Name}
                      getOptionValue={(option: LayerMicroSP) => String(option.LayerId)}
                      arrowRenderer={() => null}
                      styles={selectStyles}
                    />
                  </div>
                </div>
              )}

              {/* Loading Spinner */}
              {isLoading && (
                <div className="text-center">
                  <div className="ifj-spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              )}
              <div className="flex flex-row justify-between">
                <button
                  className="bg-corgee-blue hover:bg-corgee-BlueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-[6px]"
                  onClick={e => getTemplateFile(e)}
                >
                  Download Template
                </button>
                <div className="text-right gap-[10px] relative overflow-hidden min-h-6 flex ">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="bg-brightGray hover:bg-corgee-brightGrayHover dark:bg-[#5c6060] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={parseDataFromFile}
                    disabled={isImportDisabled}
                    className="bg-corgee-blue hover:bg-corgee-BlueHover dark:bg-[#3c6c80] dark:hover:brightness-[0.85] disabled:opacity-65 text-white text-xs h-7 px-3 py-1"
                  >
                    Import
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Response/Success Message */}
          {responseMessage !== '' && (
            <div>
              <div className="corgee-tool-module-instructions ifj-response-size">
                <span>{responseMessage}</span>
                <ul className="ifj-ul-error-list">
                  {errorMessages.length > 0 &&
                    errorMessages.map((error, index) => (
                      <li
                        key={index}
                        className={index === 0 ? 'ifj-li-red-error' : 'ifj-li-suggestion'}
                      >
                        {error}
                      </li>
                    ))}
                </ul>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-brightGray hover:bg-corgee-brightGrayHover dark:bg-[#5c6060] dark:hover:brightness-[0.85] text-white text-xs h-7 px-3 py-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
